/**
 * TagsDisplaySection Component
 * 
 * Упрощенный компонент для отображения тегов и кнопки управления
 * Заменяет сложную логику управления тегами в RecordHeader
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { TagChip } from './TagChip';
import { Hash } from 'lucide-react';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from "react-i18next";

interface TagsDisplaySectionProps {
  tags: Tag[];
  onManageClick: () => void;
  onTagClick?: () => void;
  onNavigateToRecords?: (tag: Tag) => void;
  onEditTag?: (tag: Tag) => void;
  className?: string;
  disabled?: boolean;
}

export const TagsDisplaySection: React.FC<TagsDisplaySectionProps> = ({
  tags,
  onManageClick,
  onTagClick,
  onNavigateToRecords,
  onEditTag,
  className = '',
  disabled = false
}) => {
  const { t } = useTranslation();
  const hasTagsToDisplay = tags && tags.length > 0;

  return (
    <div
      data-testid="tags-display-section"
      className={`flex flex-col gap-2 min-w-0 ${className}`}
    >
      {/* Отображение тегов */}
      {hasTagsToDisplay && (
        <div className="flex flex-wrap gap-1">
          {tags.map(tag => (
            <TagChip
              key={tag.id}
              tag={tag}
              size="sm"
              showContextMenu={!disabled}
              onNavigateToRecords={onNavigateToRecords}
              onEdit={onEditTag}
              // В режиме просмотра теги не удаляются напрямую
              onDelete={undefined}
              onRemove={undefined}
            />
          ))}
        </div>
      )}

      {/* Кнопка управления тегами - показываем только когда тегов нет */}
      {!hasTagsToDisplay && !disabled && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onManageClick}
          className="text-xs text-muted-foreground hover:text-foreground w-fit px-0 h-6"
          type="button"
          aria-label={t('records.tags_display.add_tags_aria')}
        >
          <Hash className="h-4 w-4 mr-1" />
          {t('records.tags_display.add_tags')}
        </Button>
      )}
    </div>
  );
};